import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { notify } from "reapop";

/**
 * Хук для копирования содержимого ячейки по правому клику.
 *
 * @param {Object} options
 * @param {string} [options.successImage] — путь к картинке уведомления
 * @param {string[]} [options.excludeTexts] — тексты-плейсхолдеры, которые не копируем
 * @returns {{ handleCellContextMenu: Function }}
 */
export const useCellContextCopy = ({
  successImage = "/public/assets/images/illustrations/check.svg",
  excludeTexts = ["Выбрать", "—", "-"],
} = {}) => {
  const dispatch = useDispatch();

  const fallbackCopy = useCallback((text) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");
    } finally {
      document.body.removeChild(textarea);
    }
  }, []);

  const copyToClipboard = useCallback(
    (text) => {
      if (!text) return;
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).catch(() => fallbackCopy(text));
      } else {
        fallbackCopy(text);
      }
    },
    [fallbackCopy]
  );

  const handleCellContextMenu = useCallback(
    (e, cellProps) => {
      e.preventDefault();
      e.stopPropagation();

      const header = cellProps.header || cellProps.id || "";
      let valueToCopy = "";

      // Сначала берём отображаемый текст из DOM
      if (e.currentTarget) {
        const clone = e.currentTarget.cloneNode(true);
        clone
          .querySelectorAll("button, .material-icons, svg")
          .forEach((el) => el.remove());
        const cleanText = (clone.innerText || clone.textContent || "").trim();
        if (cleanText && !excludeTexts.includes(cleanText)) {
          valueToCopy = cleanText;
        }
      }

      if (!valueToCopy && cellProps.value !== undefined && cellProps.value !== null) {
        valueToCopy = String(cellProps.value).trim();
      }

      if (!valueToCopy) return;

      copyToClipboard(valueToCopy);

      dispatch(
        notify({
          title: "Успешно",
          message: `Поле "${header}" скопировано.`,
          status: "success",
          image: successImage,
        })
      );
    },
    [copyToClipboard, dispatch, excludeTexts, successImage]
  );

  return { handleCellContextMenu };
};

export default useCellContextCopy;